import type { I18n } from '@learnforge/i18n';
import { createSvgEl } from '@learnforge/demo-render';

export interface FlowchartBox {
  id: string;
  labelKey: string;
  valueKey?: string;
  position: { col: number; row: number };
}

export interface FlowchartEdge {
  from: string;
  to: string;
  labelKey?: string;
}

export interface FlowchartOptions {
  boxes: readonly FlowchartBox[];
  edges: readonly FlowchartEdge[];
  values: Readonly<Record<string, number>>;
  i18n: I18n<string>;
}

export interface FlowchartHandle {
  group: SVGGElement;
  setValues(values: Readonly<Record<string, number>>): void;
  stepAnimation(): void;
  destroy(): void;
}

const COL_W = 180;
const ROW_H = 100;
const BOX_W = 140;
const BOX_H = 60;
const PAD = 20;

export function createFlowchart(parent: SVGGElement, opts: FlowchartOptions): FlowchartHandle {
  const group = createSvgEl('g', { class: 'pb-flow' });
  parent.appendChild(group);
  const boxRefs = new Map<string, { rect: SVGRectElement; valueText: SVGTextElement | null }>();
  const edgeRefs: SVGLineElement[] = [];
  let currentValues = { ...opts.values };
  let liveEdgeIndex = -1;

  function center(id: string): { x: number; y: number } {
    const box = opts.boxes.find((b) => b.id === id)!;
    return {
      x: PAD + box.position.col * COL_W + BOX_W / 2,
      y: PAD + box.position.row * ROW_H + BOX_H / 2,
    };
  }

  function formatValue(v: number | undefined): string {
    if (v === undefined) return '';
    return v.toFixed(4);
  }

  function renderInitial(): void {
    for (const e of opts.edges) {
      const a = center(e.from);
      const b = center(e.to);
      const line = createSvgEl('line', {
        class: 'pb-flow-edge',
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
      });
      group.appendChild(line);
      edgeRefs.push(line);
    }
    for (const b of opts.boxes) {
      const x = PAD + b.position.col * COL_W;
      const y = PAD + b.position.row * ROW_H;
      const rect = createSvgEl('rect', {
        class: 'pb-flow-box',
        x,
        y,
        width: BOX_W,
        height: BOX_H,
        rx: 6,
      });
      group.appendChild(rect);
      const lbl = createSvgEl('text', {
        class: 'pb-flow-box-label',
        x: x + BOX_W / 2,
        y: y + BOX_H / 2 - 6,
        'text-anchor': 'middle',
      });
      lbl.textContent = opts.i18n.t(b.labelKey);
      group.appendChild(lbl);
      let valueText: SVGTextElement | null = null;
      if (b.valueKey !== undefined || currentValues[b.id] !== undefined) {
        valueText = createSvgEl('text', {
          class: 'pb-flow-box-value',
          x: x + BOX_W / 2,
          y: y + BOX_H / 2 + 14,
          'text-anchor': 'middle',
        });
        valueText.textContent = formatValue(currentValues[b.id]);
        group.appendChild(valueText);
      }
      boxRefs.set(b.id, { rect, valueText });
    }
  }

  function applyValues(): void {
    for (const [id, refs] of boxRefs) {
      if (refs.valueText) {
        refs.valueText.textContent = formatValue(currentValues[id]);
      }
    }
  }

  function applyLive(): void {
    edgeRefs.forEach((el, i) => {
      if (i === liveEdgeIndex) el.classList.add('pb-flow-edge-live');
      else el.classList.remove('pb-flow-edge-live');
    });
  }

  renderInitial();

  return {
    group,
    setValues(values) {
      currentValues = { ...values };
      applyValues();
    },
    stepAnimation() {
      liveEdgeIndex = liveEdgeIndex + 1 >= edgeRefs.length ? -1 : liveEdgeIndex + 1;
      applyLive();
    },
    destroy() {
      group.remove();
    },
  };
}
